import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import BrandPanel from "../components/BrandPanel";
import LangToggle from "../components/LangToggle";
import { useLang } from "../context/LangContext";
import { UserCheck, Lock, Building2 } from "lucide-react";
import { api, formatApiError } from "../lib/api";

export default function EmployeeLogin() {
    const { t, lang, toggle } = useLang();
    const { refresh } = useAuth();
    const navigate = useNavigate();
    const [employeeName, setEmployeeName] = useState("");
    const [companyCode, setCompanyCode] = useState("");
    const [password, setPassword] = useState("");
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");

    const submit = async (e) => {
        e.preventDefault();
        setError("");
        setLoading(true);
        try {
            await api.post("/auth/employee/login", {
                employee_name: employeeName.trim(),
                company_code: companyCode.trim().toUpperCase(),
                password,
            });
            await refresh();
            navigate("/");
        } catch (err) {
            setError(formatApiError(err.response?.data?.detail) || err.message);
        } finally {
            setLoading(false);
        }
    };

    const iconSide = lang === "ar" ? { right: "0.75rem" } : { left: "0.75rem" };
    const inputPad = lang === "ar" ? "h-11 pe-10 ps-3" : "h-11 ps-10 pe-3";

    return (
        <div className="min-h-screen app-bg" dir={lang === "ar" ? "rtl" : "ltr"}>
            <div className="grid min-h-screen lg:grid-cols-2">
                <BrandPanel lang={lang} strings={t} ribbon={t.employeeLoginTitle} />
                <div className="flex items-center justify-center px-4 py-8 sm:px-8 sm:py-12">
                    <div className="w-full max-w-md">
                        <div className="mb-6 flex items-center justify-between">
                            <Link to="/login" className="inline-flex items-center gap-1 rounded-md border border-neutral-200 bg-white px-3 py-1.5 text-xs font-semibold text-neutral-700 shadow-sm hover:bg-neutral-50" data-testid="emp-login-home-btn">
                                {t.accountTypeBackHome}
                            </Link>
                            <LangToggle lang={lang} onToggle={toggle} t={t} />
                        </div>
                        <h2 className="mb-6 font-heading text-2xl font-bold sm:text-3xl" data-testid="emp-login-title">{t.employeeLoginTitle}</h2>
                        <form onSubmit={submit} className="space-y-4" data-testid="emp-login-form">
                            <div>
                                <Label className="mb-1.5 block text-sm font-medium">{t.employeeCodeLabel}</Label>
                                <div className="relative">
                                    <Building2 className="pointer-events-none absolute top-1/2 h-4 w-4 -translate-y-1/2 text-neutral-400" style={iconSide} />
                                    <Input
                                        value={companyCode}
                                        onChange={(e) => setCompanyCode(e.target.value.toUpperCase())}
                                        className={`${inputPad} font-numbers uppercase tracking-wider`}
                                        placeholder={t.employeeCodePlaceholder}
                                        required
                                        data-testid="emp-login-code"
                                        dir="ltr"
                                    />
                                </div>
                            </div>
                            <div>
                                <Label className="mb-1.5 block text-sm font-medium">{t.employeeNameLabel}</Label>
                                <div className="relative">
                                    <UserCheck className="pointer-events-none absolute top-1/2 h-4 w-4 -translate-y-1/2 text-neutral-400" style={iconSide} />
                                    <Input
                                        value={employeeName}
                                        onChange={(e) => setEmployeeName(e.target.value)}
                                        className={inputPad}
                                        placeholder={t.employeeNamePlaceholder}
                                        required
                                        data-testid="emp-login-name"
                                    />
                                </div>
                            </div>
                            <div>
                                <Label className="mb-1.5 block text-sm font-medium">{t.passwordLabel}</Label>
                                <div className="relative">
                                    <Lock className="pointer-events-none absolute top-1/2 h-4 w-4 -translate-y-1/2 text-neutral-400" style={iconSide} />
                                    <Input
                                        type="password"
                                        value={password}
                                        onChange={(e) => setPassword(e.target.value)}
                                        className={inputPad}
                                        placeholder={t.passwordPlaceholder}
                                        required
                                        data-testid="emp-login-password"
                                    />
                                </div>
                            </div>
                            {error ? (
                                <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700" data-testid="emp-login-error">
                                    {error}
                                </div>
                            ) : null}
                            <Button
                                type="submit"
                                disabled={loading}
                                className="h-12 w-full bg-neutral-900 text-white hover:bg-neutral-800"
                                data-testid="emp-login-submit"
                            >
                                {loading ? "…" : t.loginCta}
                            </Button>
                        </form>
                    </div>
                </div>
            </div>
        </div>
    );
}
